import React, { useState, useEffect, useCallback } from 'react'
import {
  Bell, AlertTriangle, CheckCircle, Send, Webhook,
  Clock, RefreshCw, Check, Mail, Save, Info
} from 'lucide-react'
import {
  getAlerts, getAlertSettings, saveAlertSettings,
  testAlertWebhook, testAlertEmail, getClients, acknowledgeIncident
} from '../api'

export default function AlertHub() {
  const [webhookUrl, setWebhookUrl] = useState('')
  const [recipientEmails, setRecipientEmails] = useState('')
  const [testingWebhook, setTestingWebhook] = useState(false)
  const [testingEmail, setTestingEmail] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [smtpConfigured, setSmtpConfigured] = useState(true)
  const [testResult, setTestResult] = useState(null)
  const [emailTestResult, setEmailTestResult] = useState(null)
  const [saveSuccessMsg, setSaveSuccessMsg] = useState(null)
  const [activeFiring, setActiveFiring] = useState([])
  const [resolvedList, setResolvedList] = useState([])
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [loading, setLoading] = useState(true)
  const [acknowledgingId, setAcknowledgingId] = useState(null)

  const fetchAlerts = useCallback(async () => {
    setLoading(true)
    try {
      const [alertData, clientData, settingsData] = await Promise.all([
        getAlerts(selectedClient || null),
        getClients().catch(() => ({ clients: [] })),
        getAlertSettings(selectedClient || null).catch(() => null)
      ])
      setActiveFiring(Array.isArray(alertData?.active_alerts) ? alertData.active_alerts : [])
      setResolvedList(Array.isArray(alertData?.history) ? alertData.history.filter(h => h.status === 'RESOLVED') : [])
      if (Array.isArray(clientData?.clients)) {
        setClients(clientData.clients)
      }
      if (settingsData) {
        if (settingsData.webhook_url !== undefined && !webhookUrl) {
          setWebhookUrl(settingsData.webhook_url || '')
        }
        if (settingsData.recipient_emails !== undefined) {
          setRecipientEmails(settingsData.recipient_emails || '')
        }
        setSmtpConfigured(Boolean(settingsData.smtp_configured))
      }
    } catch {
      setActiveFiring([])
      setResolvedList([])
    } finally {
      setLoading(false)
    }
  }, [selectedClient])

  useEffect(() => {
    fetchAlerts()
  }, [fetchAlerts])

  const handleTestWebhook = async () => {
    if (!webhookUrl.trim()) {
      setTestResult({ success: false, msg: 'Please enter a valid destination webhook URL first.' })
      return
    }
    setTestingWebhook(true)
    setTestResult(null)
    try {
      const res = await testAlertWebhook(webhookUrl.trim())
      setTestResult({ success: true, msg: res?.message || 'Test alert dispatched to webhook listener.' })
      fetchAlerts()
    } catch (err) {
      setTestResult({ success: false, msg: err?.message || 'Webhook dispatch failed.' })
    } finally {
      setTestingWebhook(false)
    }
  }

  const handleTestEmail = async () => {
    if (!recipientEmails.trim()) {
      setEmailTestResult({ success: false, msg: 'Please enter at least one recipient email address first.' })
      return
    }
    setTestingEmail(true)
    setEmailTestResult(null)
    try {
      const res = await testAlertEmail(recipientEmails.trim())
      if (res.delivered) {
        setEmailTestResult({ success: true, msg: res.message || 'Test alert email delivered successfully.' })
      } else {
        setEmailTestResult({ success: false, msg: res.detail || 'Email dispatch failed. Verify server SMTP configuration.' })
      }
    } catch (err) {
      setEmailTestResult({ success: false, msg: err?.message || 'Email delivery failed.' })
    } finally {
      setTestingEmail(false)
    }
  }

  const handleSaveSettings = async () => {
    setSavingSettings(true)
    setSaveSuccessMsg(null)
    try {
      await saveAlertSettings({
        client_id: selectedClient || null,
        webhook_url: webhookUrl.trim(),
        recipient_emails: recipientEmails.trim()
      })
      setSaveSuccessMsg('Notification endpoints and recipient emails saved successfully.')
      setTimeout(() => setSaveSuccessMsg(null), 4000)
      fetchAlerts()
    } catch (err) {
      alert(`Could not save alert settings: ${err.message}`)
    } finally {
      setSavingSettings(false)
    }
  }

  const handleAcknowledge = async (incidentId) => {
    setAcknowledgingId(incidentId)
    try {
      await acknowledgeIncident(incidentId, 'Acknowledged by operator in Alert Hub')
      await fetchAlerts()
    } catch (err) {
      alert(`Could not acknowledge incident: ${err.message}`)
    } finally {
      setAcknowledgingId(null)
    }
  }

  return (
    <div className="animate-in" style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Bell size={24} style={{ color: 'var(--brand-coral)' }} />
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
              CloudIQ Pulse — Alert &amp; Incident Hub
            </h2>
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-600)', fontSize: 13 }}>
            Real-time incident state tracking across CloudIQ Pulse agents, application health monitors, and AWS CloudWatch thresholds.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="input-select"
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              fontSize: 13,
              fontWeight: 600,
              background: '#FFFFFF',
              boxShadow: 'var(--shadow-solid-sm)'
            }}
          >
            <option value="">All Monitored Clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={fetchAlerts}
            className="btn btn-secondary btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              borderRadius: 8,
              fontWeight: 700
            }}
          >
            <RefreshCw size={13} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Notification Channel Settings */}
      <div style={{
        background: '#FFFFFF',
        padding: 22,
        borderRadius: 12,
        border: '2px solid var(--border-ink)',
        boxShadow: 'var(--shadow-solid-md)',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Webhook size={18} style={{ color: 'var(--brand-blue)' }} />
          <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
            Multi-Channel Dispatch Endpoints (SNS Alternative)
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
          {/* JSON Webhook */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-800)', marginBottom: 6, textTransform: 'uppercase' }}>
              Generic JSON Webhook URL (Slack / PagerDuty / Automation)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="https://hooks.slack.com/services/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="input-text font-mono"
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
              />
              <button
                type="button"
                onClick={handleTestWebhook}
                disabled={testingWebhook}
                className="btn btn-primary btn-sm"
                style={{ borderRadius: 8, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, boxShadow: 'var(--shadow-solid-sm)' }}
              >
                {testingWebhook ? <RefreshCw size={12} className="spin" /> : <Send size={12} />}
                Test Webhook
              </button>
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 6, display: 'block' }}>
              Receives instant JSON payloads on both issue creation (FIRING) and recovery (RESOLVED).
            </span>
          </div>

          {/* Email Recipients */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-800)', marginBottom: 6, textTransform: 'uppercase' }}>
              Email Recipients (HTML Incident &amp; Recovery Cards)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="ops@adamastech.in, alerts@adamastech.in"
                value={recipientEmails}
                onChange={(e) => setRecipientEmails(e.target.value)}
                className="input-text font-mono"
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
              />
              <button
                type="button"
                onClick={handleTestEmail}
                disabled={testingEmail}
                className="btn btn-primary btn-sm"
                style={{ borderRadius: 8, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, boxShadow: 'var(--shadow-solid-sm)', background: 'var(--brand-blue)' }}
              >
                {testingEmail ? <RefreshCw size={12} className="spin" /> : <Mail size={12} />}
                Test Email
              </button>
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 6, display: 'block' }}>
              Comma-separated. Alerts dispatch immediately on breach transition (OK to FIRING) and recovery (RESOLVED).
            </span>
          </div>
        </div>

        {/* Action Toolbar & SMTP Diagnostic Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: '1px solid #E5E7EB', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <span style={{
              display: 'inline-block',
              width: 9,
              height: 9,
              borderRadius: '50%',
              backgroundColor: smtpConfigured ? '#10B981' : '#F59E0B'
            }} />
            <span style={{ fontWeight: 600, color: smtpConfigured ? '#047857' : '#B45309' }}>
              {smtpConfigured ? 'SMTP Relay Configured & Active' : 'SMTP Delivery Not Configured on Server'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="btn btn-secondary btn-sm"
            style={{
              borderRadius: 8,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)'
            }}
          >
            {savingSettings ? <RefreshCw size={12} className="spin" /> : <Save size={12} />}
            Save Notification Endpoints
          </button>
        </div>

        {saveSuccessMsg && (
          <div style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1.5px solid #10B981',
            background: '#DEF7EC',
            color: '#03543F',
            fontSize: 12.5,
            fontWeight: 600
          }}>
            {saveSuccessMsg}
          </div>
        )}

        {testResult && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1.5px solid var(--border-ink)',
            fontSize: 13,
            fontWeight: 600,
            background: testResult.success ? '#DEF7EC' : 'var(--brand-coral-bg)',
            color: testResult.success ? '#03543F' : 'var(--brand-coral)',
            boxShadow: 'var(--shadow-solid-sm)'
          }}>
            {testResult.msg}
          </div>
        )}

        {emailTestResult && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1.5px solid var(--border-ink)',
            fontSize: 13,
            fontWeight: 600,
            background: emailTestResult.success ? '#DEF7EC' : 'var(--brand-coral-bg)',
            color: emailTestResult.success ? '#03543F' : 'var(--brand-coral)',
            boxShadow: 'var(--shadow-solid-sm)'
          }}>
            {emailTestResult.msg}
          </div>
        )}
      </div>

      {/* Active Incidents (FIRING) */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
          <AlertTriangle size={18} style={{ color: 'var(--brand-coral)' }} />
          <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--brand-coral)' }}>
            Active Incidents — Currently FIRING ({activeFiring.length})
          </h3>
        </div>

        {activeFiring.length === 0 ? (
          <div style={{
            background: '#FFFFFF',
            padding: 32,
            borderRadius: 12,
            border: '2px solid var(--border-ink)',
            boxShadow: 'var(--shadow-solid-sm)',
            textAlign: 'center',
            color: 'var(--ink-600)',
            fontSize: 14
          }}>
            <CheckCircle size={24} style={{ color: '#047857', margin: '0 auto 8px auto', display: 'block' }} />
            No active incidents. All monitored hosts and services operating within healthy thresholds.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
            {activeFiring.map((alert, idx) => (
              <div
                key={alert.id || idx}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 10,
                  border: '2px solid var(--border-ink)',
                  borderLeft: '6px solid var(--brand-coral)',
                  padding: 18,
                  boxShadow: 'var(--shadow-solid-sm)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="badge badge-alarm" style={{ fontSize: 11, fontWeight: 800 }}>
                    {alert.severity || 'FIRING'}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--ink-500)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={12} /> {alert.timestamp || alert.triggered_at || 'Active'}
                  </span>
                </div>
                <h4 style={{ margin: '10px 0 4px', fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                  {alert.summary || alert.description || `${alert.metric_name || 'Metric'} alert on ${alert.hostname}`}
                </h4>
                <div style={{ fontSize: 12.5, color: 'var(--ink-600)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                  <div>
                    {alert.client_name ? <span>Client: <strong>{alert.client_name}</strong></span> : null}
                  </div>
                  <div>
                    {alert.acknowledged_at ? (
                      <span className="badge badge-ok" style={{ fontSize: 10.5 }}>Acknowledged</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAcknowledge(alert.id)}
                        disabled={acknowledgingId === alert.id}
                        className="btn btn-secondary btn-sm"
                        style={{ border: '1px solid var(--border-ink)', borderRadius: 4, fontSize: 11, fontWeight: 700 }}
                      >
                        {acknowledgingId === alert.id ? 'Saving…' : 'Acknowledge'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resolved Incidents (RESOLVED) */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
          <CheckCircle size={18} style={{ color: '#047857' }} />
          <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: '#047857' }}>
            Recently Resolved Incidents ({resolvedList.length})
          </h3>
        </div>

        {resolvedList.length === 0 ? (
          <div style={{
            background: '#FFFFFF',
            padding: 24,
            borderRadius: 12,
            border: '2px solid var(--border-ink)',
            boxShadow: 'var(--shadow-solid-sm)',
            textAlign: 'center',
            color: 'var(--ink-500)',
            fontSize: 13
          }}>
            No resolved incident transitions recorded in this session.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
            {resolvedList.map((alert, idx) => (
              <div
                key={alert.id || idx}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 10,
                  border: '2px solid var(--border-ink)',
                  borderLeft: '6px solid #10B981',
                  padding: 18,
                  boxShadow: 'var(--shadow-solid-sm)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="badge badge-ok" style={{ fontSize: 11, fontWeight: 800 }}>
                    RESOLVED
                  </span>
                  <span style={{ fontSize: 12, color: '#047857', fontWeight: 600 }}>
                    {alert.resolved_at || alert.timestamp || ''}
                  </span>
                </div>
                <h4 style={{ margin: '10px 0 4px', fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                  {alert.summary || alert.description || `${alert.metric_name || 'Metric'} returned to normal`}
                </h4>
                {alert.outage_duration_seconds ? (
                  <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4 }}>
                    Outage duration: <strong>{Math.round(alert.outage_duration_seconds / 60)} minutes</strong>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
